import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { ScrollArea } from "@component/scroll-area";
import { RadioGroup, RadioGroupItem } from "@component/radio-group";
import { FileClockIcon, RefreshCwIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Job, JobUserHistoryItem } from "@type/job";
import { describeGate, HiringFlow, HiringFlowStep, STEP_TYPE_LABELS, stepGates } from "@type/hiring-flow";
import { formatHumanReadable, parseFormat } from "@util/date";
import { getJobVersions } from "@service/job";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    job: Job
}

export default function VersionHistoryDialog(props: Props) {
    const [histories, setHistories] = useState<JobUserHistoryItem[]>([]);
    const [selectedVersion, setSelectedVersion] = useState<JobUserHistoryItem | null>(null);

    useEffect(() => {
        if (props.isOpen) {
            getJobVersions(props.job.id).then((resp) => {
                setHistories(resp.data.data);
                if (resp.data.data.length > 0) {
                    setSelectedVersion(resp.data.data[0]);
                } else {
                    setSelectedVersion(null);
                }
            });
        }
    }, [props.isOpen])

    const getFieldLabel = (field: string) => {
        const labels: Record<string, string> = {
            job_status: "Status",
            job_title: "Job Title",
            company_name: "Company Name",
            department: "Department",
            location: "Location",
            job_type: "Employment Type",
            job_description: "Description",
            requirements: "Requirements",
            is_public: "Public Visibility",
            min_rate: "Minimum Rate",
            max_rate: "Maximum Rate",
            rate_currency: "Rate Currency",
            fixed_rate: "Fixed Rate",
            assessment_ids: "Screenings",
            referral_currency: "Referral Currency",
            referral_bonus: "Referral Bonus",
            pay_rate_frequency: "Pay Rate Frequency",
            days_auto_rejection: "Days until Auto-rejection",
            is_enable_auto_rejection: "Enable Auto-rejection",
            masked_benefits: "Benefits",
            masked_requirements: "Requirements",
            outgoing_email: "Outgoing Email",
            rejection_email_subject: "Rejection Email Subject",
            rejection_email_body: "Rejection Email Body",
            is_enable_work_authorization: "Require Work Authorization",
            work_authorization_metadata: "Eligibility Checks",
            hiring_flow: "Hiring Flow"
        };
        return labels[field] || field;
    };

    const isJsonString = (str: string) => {
        if (!str || str === "Not set") return false;
        const trimmed = str.trim();
        return (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
            (trimmed.startsWith("[") && trimmed.endsWith("]"));
    };

    const renderJsonValue = (value: string): React.ReactNode => {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                if (parsed.length === 0) return "—";
                return (
                    <ul className="list-disc list-inside space-y-0.5">
                        {parsed.map((item, i) => (
                            <li key={i} className="text-sm">
                                {typeof item === "object" && item !== null
                                    ? Object.entries(item).map(([k, v]) => `${k}: ${typeof v === "string" ? stripHtml(v) : v}`).join(", ")
                                    : stripHtml(String(item))}
                            </li>
                        ))}
                    </ul>
                );
            }
            if (typeof parsed === "object" && parsed !== null) {
                const entries = Object.entries(parsed).filter(([, v]) => v !== null && v !== "");
                if (entries.length === 0) return "—";
                return (
                    <div className="space-y-1">
                        {entries.map(([key, val]) => (
                            <div key={key} className="flex gap-2 text-sm">
                                <span className="text-muted-foreground min-w-[120px]">{getFieldLabel(key) !== key ? getFieldLabel(key) : key.replace(/_/g, " ")}:</span>
                                <span>{typeof val === "string" ? stripHtml(val) : typeof val === "object" ? JSON.stringify(val) : String(val)}</span>
                            </div>
                        ))}
                    </div>
                );
            }
            return String(parsed);
        } catch {
            return value || "—";
        }
    };

    const stripHtml = (str: string, isNewValue = false) => {
        return str
            .replace(/\n/g, isNewValue ? "" : "<br />")
            .replace(/<br\s*\/?>/gi, "<br />")
            .replace(/(<br \/>){2,}/g, "<br />")
            .trim();
    };

    const getAssessmentName = (name: string) => name === "assessment_ids" ? "General 20-min" : name;

    const getAssessmentTypeLabel = (type: string) => {
        const labels: Record<string, string> = {
            "Interview": "AI Interview",
            "Form": "Form",
        };
        return labels[type] || type;
    };

    const renderAssessmentChanges = (oldValue: string, newValue: string): React.ReactNode => {
        try {
            const safeParse = (val: string) => {
                if (!val || val === "Not set") return [];
                return JSON.parse(val);
            };
            const oldParsed: { type: string; id: string; name: string }[] = safeParse(oldValue);
            const newParsed: { type: string; id: string; name: string }[] = safeParse(newValue);

            const allTypes = new Set([
                ...oldParsed.map((item) => item.type),
                ...newParsed.map((item) => item.type),
            ]);

            if (allTypes.size === 0) return "—";

            console.log('allTypes', allTypes)

            return (
                <div className="space-y-2">
                    {Array.from(allTypes).map((type) => {
                        const oldItem = oldParsed.find((item) => item.type === type);
                        const newItem = newParsed.find((item) => item.type === type);
                        const oldName = oldItem ? getAssessmentName(oldItem.name) : null;
                        const newName = newItem ? getAssessmentName(newItem.name) : null;

                        return (
                            <div key={type}>
                                <p className="text-sm text-muted-foreground">{getAssessmentTypeLabel(type)}</p>
                                <div className="flex items-center gap-2 text-sm">
                                    {oldName && newName && oldName !== newName ? (
                                        <>
                                            <div className="text-muted-foreground">{oldName}</div>
                                            <span className="text-muted-foreground">→</span>
                                            <div className="">{newName}</div>
                                        </>
                                    ) : oldName && !newName ? (
                                        <div className="text-muted-foreground">{oldName}</div>
                                    ) : newName && !oldName ? (
                                        <>
                                                    <div className="text-muted-foreground">{type === "Interview" ? "General 20-min" : "-"}</div>
                                                    <span className="text-muted-foreground">→</span>
                                                    <div className="">{newName}</div>
                                        </>
                                    ) : (
                                        <>
                                                        <div className="text-muted-foreground">{type === "Interview" ? "General 20-min" : ""}</div>
                                                        <span className="text-muted-foreground">→</span>
                                                        <div className="">{newName}</div>
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        } catch (error) {
            console.error(error)
            return "—";
        }
    };

    const renderValue = (value: string, field: string, isNewValue = false): React.ReactNode => {
        if (!value) return "—";

        if (value === "true") return "Yes";
        if (value === "false") return "No";

        if (field === "work_authorization_metadata") {
            try {
                const parsed = JSON.parse(value);
                const workAuthLabels: Record<string, string> = {
                    collect_employment_status: "Current employment status",
                    collect_expected_salary: "Expected salary",
                    collect_start_availability: "Earliest start date",
                    collect_current_salary: "Current or Last drawn salary",
                    collect_relocation_willingness: "Willingness to relocate",
                    collect_weekend_shift_willingness: "Willingness to work overtime",
                };
                const fields = Object.entries(parsed)
                    .filter(([key]) => workAuthLabels[key])
                    .map(([key, v]) => ({ label: workAuthLabels[key], value: v === true ? "Yes" : "No" }));
                if (fields.length === 0) return "—";
                return (
                    <ul className="list-disc list-inside space-y-0.5">
                        {fields.map((item, i) => (
                            <li key={i} className="text-sm">{item.label}: {item.value}</li>
                        ))}
                    </ul>
                );
            } catch {
                return value;
            }
        } else if (field === "outgoing_email") {
            try {
                const parsed = JSON.parse(value);
                const outgoing_emails: Record<string, string> = {
                    sender_name: "Default Sender Name",
                    default_reply_to: "Default Reply-to"
                };
                const fields = Object.entries(parsed)
                    .filter(([key]) => outgoing_emails[key] && key !== "is_job_specific_outgoing_email")
                    .map(([key, v]) => ({ label: outgoing_emails[key], value: String(v || "Not set") }));
                if (fields.length === 0) return "—";
                return (
                    <ul className="list-disc list-inside space-y-0.5">
                        {fields.map((item, i) => (
                            <li key={i} className="text-sm">{item.label}: {item.value}</li>
                        ))}
                    </ul>
                );
            } catch {
                return value;
            }
        }

        if (field === "assessment_ids") {
            try {
                const parsed = JSON.parse(value || "[]");
                if (parsed.length === 0) return "—";
                return parsed.map((item: { name: string }) =>
                    item.name === "assessment_ids" ? "General 20-min" : item.name
                ).join(", ");
            } catch {
                return value === "assessment_ids" ? "General 20-min" : value;
            }
        }

        if (isJsonString(value)) {
            return renderJsonValue(value);
        }

        const cleaned = stripHtml(value, isNewValue);
        if (!cleaned) return "—";
        return <span dangerouslySetInnerHTML={{ __html: cleaned }} />;
    };

    /**
     * Hiring flow (#3066) — the raw JSONB is hundreds of lines, so dumping it
     * would be unreadable. Summarize what actually moved between the two
     * versions: channel, how candidates enter, and the step list (added /
     * removed / reordered / condition count). Anything unparseable falls back
     * to "Flow updated" rather than an empty panel.
     */
    const renderHiringFlowChanges = (oldValue: string, newValue: string): React.ReactNode => {
        const parse = (raw: string): HiringFlow | null => {
            try {
                const parsed = JSON.parse(raw || "");
                return parsed && typeof parsed === "object" ? (parsed as HiringFlow) : null;
            } catch {
                return null;
            }
        };
        const before = parse(oldValue);
        const after = parse(newValue);
        if (!after) return <span className="text-sm text-muted-foreground">Flow updated</span>;

        const channelLabel = (c?: string) => (c === "whatsapp_first" ? "WhatsApp-first" : "Web");
        const entryLabel = (autoStart?: boolean) => (autoStart ? "Auto-start" : "HR sends invitation");
        const stepName = (step: HiringFlowStep) =>
            step.label || STEP_TYPE_LABELS[step.type] || step.type;

        const lines: string[] = [];

        if (before && before.channel !== after.channel) {
            lines.push(`Channel: ${channelLabel(before.channel)} → ${channelLabel(after.channel)}`);
        }
        if (before && before.entry?.auto_start !== after.entry?.auto_start) {
            lines.push(`Candidates enter by: ${entryLabel(before.entry?.auto_start)} → ${entryLabel(after.entry?.auto_start)}`);
        }
        if (before && before.entry?.cv_required_at_apply !== after.entry?.cv_required_at_apply) {
            lines.push(`CV required to apply: ${before.entry?.cv_required_at_apply ? "Yes" : "No"} → ${after.entry?.cv_required_at_apply ? "Yes" : "No"}`);
        }

        const beforeSteps = before?.steps || [];
        const afterSteps = after.steps || [];
        const beforeById = new Map(beforeSteps.map((s) => [s.id, s]));
        const afterById = new Map(afterSteps.map((s) => [s.id, s]));

        afterSteps.filter((s) => !beforeById.has(s.id)).forEach((s) => {
            lines.push(`Step added: ${stepName(s)}`);
        });
        beforeSteps.filter((s) => !afterById.has(s.id)).forEach((s) => {
            lines.push(`Step removed: ${stepName(s)}`);
        });

        // Order only counts as a change when the same steps are present — an
        // add/remove already shifts positions and is reported above.
        const sameSet = beforeSteps.length === afterSteps.length
            && beforeSteps.every((s) => afterById.has(s.id));
        if (before && sameSet && beforeSteps.map((s) => s.id).join() !== afterSteps.map((s) => s.id).join()) {
            lines.push(`Steps reordered: ${afterSteps.map(stepName).join(" → ")}`);
        }

        afterSteps.forEach((step) => {
            const previous = beforeById.get(step.id);
            if (!previous) return;
            // Name the conditions rather than counting them — "conditions: 0 → 1"
            // tells HR something changed but not what it now knocks out on.
            const describedBefore = stepGates(previous).map(describeGate);
            const describedAfter = stepGates(step).map(describeGate);
            describedAfter.filter((g) => !describedBefore.includes(g)).forEach((g) => {
                lines.push(`${stepName(step)} — condition added: ${g}`);
            });
            describedBefore.filter((g) => !describedAfter.includes(g)).forEach((g) => {
                lines.push(`${stepName(step)} — condition removed: ${g}`);
            });
            if ((previous.message || "") !== (step.message || "")) {
                lines.push(`${stepName(step)} — message updated`);
            }
            if ((previous.advance || "auto") !== (step.advance || "auto")) {
                lines.push(`${stepName(step)} — after this step: ${previous.advance === "review" ? "Pause for review" : "Continue automatically"} → ${step.advance === "review" ? "Pause for review" : "Continue automatically"}`);
            }
        });

        if (lines.length === 0) {
            // The JSON differs (e.g. the BE filled in null defaults) but nothing
            // HR configured actually moved — say so instead of showing nothing.
            return <span className="text-sm text-muted-foreground">Flow saved with no visible changes</span>;
        }

        return (
            <ul className="list-disc list-inside space-y-0.5">
                {lines.map((line, i) => (
                    <li key={i} className="text-sm">{line}</li>
                ))}
            </ul>
        );
    };

    const allowFields = [
        "hiring_flow",
        "job_status",
        "job_title",
        "company_name",
        "department",
        "location",
        "job_type",
        "job_description",
        "requirements",
        "is_public",
        "min_rate",
        "max_rate",
        "rate_currency",
        "fixed_rate",
        "assessment_ids",
        "referral_currency",
        "referral_bonus",
        "pay_rate_frequency",
        "days_auto_rejection",
        "is_enable_auto_rejection",
        "masked_benefits",
        "masked_requirements",
        "rejection_email_subject",
        "rejection_email_body",
        "outgoing_email",
        "work_authorization_metadata"
    ];

    return (
        <Dialog open={props.isOpen} onOpenChange={props.onClose}>
            <DialogContent className="max-w-6xl max-h-[80vh]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCwIcon className="h-5 w-5" />
                        Version History
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">{props.job.job_title}</p>
                </DialogHeader>
                {histories.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                            <FileClockIcon className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold mb-2">No Version History</h3>
                        <p className="text-sm text-muted-foreground max-w-sm">
                            This job hasn't been edited yet.
                            <br />
                            Any changes made to the job will appear here.
                        </p>
                    </div>
                ) : (
                    <div className="flex gap-6">
                        {/* Left side - Version list */}
                        <div className="w-64 border-r border-border pr-4">
                            <ScrollArea className="h-[500px]">
                                <RadioGroup
                                    value={selectedVersion?.id || ""}
                                    onValueChange={(value: string) => {
                                        const version = (histories || []).find((v) => v.id === value);
                                        setSelectedVersion(version || null);
                                    }}
                                    className="space-y-0"
                                >
                                    {(histories || []).map((entry, index) => (
                                        <label
                                            key={entry.id}
                                            htmlFor={entry.id}
                                            className={`flex items-center gap-3 p-3 cursor-pointer rounded-lg transition-colors ${selectedVersion?.id === entry.id
                                                ? "bg-blue-50"
                                                : "hover:bg-blue-50"
                                                }`}
                                        >
                                            <RadioGroupItem
                                                value={entry.id}
                                                id={entry.id}
                                                className="flex-shrink-0"
                                            />

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="text-sm font-medium truncate">
                                                        {index === 0
                                                            ? "Current version"
                                                            : parseFormat(
                                                                entry.action_at,
                                                                "HH:mm A, D MMM YY"
                                                            )}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <span className="text-xs text-muted-foreground truncate">
                                                        {entry.user_name}
                                                    </span>
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                </RadioGroup>
                            </ScrollArea>
                        </div>

                        {/* Right side - Version details */}
                        <div className="flex-1">
                            <ScrollArea className="h-[500px] pr-4">
                                {selectedVersion ? (
                                    <div className="space-y-4">
                                        <div className="pb-4 border-b border-border">
                                            <div className="mb-2">
                                                <div>
                                                    <p className="font-medium">
                                                        {selectedVersion.user_name}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {selectedVersion.user_email}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-muted-foreground">
                                                {formatHumanReadable(selectedVersion.action_at, true)}
                                            </p>
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-semibold mb-3">Changes Made</h4>
                                            <div className="space-y-3">
                                                {/* Nothing displayable (every change was an internal
                                                    field) — say so rather than leaving the panel blank,
                                                    which reads as a broken dialog. */}
                                                {(selectedVersion.changes || []).filter(change => allowFields.includes(change.field)).length === 0 && (
                                                    <p className="text-sm text-muted-foreground">
                                                        No changes to show for this version.
                                                    </p>
                                                )}
                                                {(selectedVersion.changes || []).filter(change => allowFields.includes(change.field)).map((change, index) => (
                                                    <div
                                                        key={index}
                                                        className="p-3 bg-muted/30 rounded-lg border border-border"
                                                    >
                                                        <p className="text-sm font-medium mb-2">
                                                            {getFieldLabel(change.field)}
                                                        </p>
                                                        {change.field === "hiring_flow" ? (
                                                            renderHiringFlowChanges(change.old_value, change.new_value)
                                                        ) : change.field === "assessment_ids" ? (
                                                            renderAssessmentChanges(change.old_value, change.new_value)
                                                        ) : isJsonString(change.old_value) || isJsonString(change.new_value) ? (
                                                                <div className="flex items-center gap-2 text-sm">
                                                                    <div className="text-muted-foreground">
                                                                        {renderValue(change.old_value, change.field)}
                                                                    </div>
                                                                    <span className="text-muted-foreground pt-0.5">→</span>
                                                                    <div>
                                                                        {renderValue(change.new_value, change.field, true)}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                                    <div className="flex items-start gap-2 text-sm">
                                                                    <span className="text-muted-foreground line-through">
                                                                        {renderValue(change.old_value, change.field)}
                                                                    </span>
                                                                    <span className="text-muted-foreground">→</span>
                                                                    <span>
                                                                        {renderValue(change.new_value, change.field, true)}
                                                                    </span>
                                                                </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-muted-foreground">
                                        Select a version to view details.
                                    </div>
                                )}
                            </ScrollArea>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
